/**
 * Rule-trace API client (PRP-003).
 *
 * Thin wrapper over GET /api/v1/user/projects/{pid}/scopes/{sid}/rule-trace.
 * Shares the same auth interceptor pattern as rulesApi.ts.
 */

import axios from 'axios';
import { auth } from './firebase';
import type { RuleTraceResponse } from '../types/trace';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[traceApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load rule trace';
    return Promise.reject(new Error(message));
  },
);

export const TraceAPI = {
  getForScope: (projectId: string, scopeId: string): Promise<RuleTraceResponse> =>
    client.get(
      `/user/projects/${encodeURIComponent(projectId)}/scopes/${encodeURIComponent(scopeId)}/rule-trace`,
    ),
};
